
import { createClient } from '@supabase/supabase-js';

// Normalize text for diacritics-insensitive comparison (handles ą→a, ę→e, ł→l etc.)
const normalizeText = (text) => {
  if (!text) return '';
  return text.toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/ą/g, 'a').replace(/ć/g, 'c').replace(/ę/g, 'e')
    .replace(/ł/g, 'l').replace(/ń/g, 'n').replace(/ó/g, 'o')
    .replace(/ś/g, 's').replace(/ź/g, 'z').replace(/ż/g, 'z');
};

// Supabase project URL and anon key
const supabaseUrl = 'https://jjbyrtlbadzrzilkwceg.supabase.co';
const supabaseKey = 'sb_publishable_t00zfmIZ4BTJjIsqmqtrpg_7wBk732X';
export const supabase = createClient(supabaseUrl, supabaseKey);

export const propertiesService = {
  // Get first N properties – used for above-the-fold priority loading
  async getFirstN(n = 4) {
    const { data, error } = await supabase
      .from('properties')
      .select('id, price, rent, area, rooms, floor, total_floors, deposit, image_url, city, street, building_number, district, available_from, amenities, equipment, title, description, first_name, phone, email, user_id, phone_reveals, views, previous_price, price_reduced_at, is_premium, premium_until, lat, lng, created_at')
      .order('is_premium', { ascending: false, nullsFirst: false })
      .order('id', { ascending: false })
      .limit(n);
    if (error) throw error;
    return data || [];
  },

  // Get all properties (always fetch from Supabase)
  async getAll() {
    
    const { data, error } = await supabase
      .from('properties')
      .select('id, price, rent, area, rooms, floor, total_floors, deposit, image_url, city, street, building_number, district, available_from, amenities, equipment, title, description, first_name, phone, email, user_id, phone_reveals, views, previous_price, price_reduced_at, is_premium, premium_until, lat, lng, created_at')
      .order('is_premium', { ascending: false, nullsFirst: false })
      .order('id', { ascending: false });
    
    
    if (error) {
      throw error;
    }
    
    if (data && data.length > 0) {
    }
    
    return data || [];
  },

  // Get properties with filters
  async getFiltered(filters = {}) {
    let query = supabase.from('properties').select('*');

    // Lokalizacja
    if (filters.city) {
      query = query.ilike('city', `%${filters.city}%`);
    }
    if (filters.district) {
      query = query.ilike('district', `%${filters.district}%`);
    }
    if (filters.street) {
      query = query.ilike('street', `%${filters.street}%`);
    }

    // Cena – obsługuje zarówno płaski format (minPrice/maxPrice)
    // jak i zagnieżdżony (priceRange.min/max) z kontekstu
    const minPrice = filters.minPrice != null ? filters.minPrice : filters.priceRange?.min;
    const maxPrice = filters.maxPrice != null ? filters.maxPrice : filters.priceRange?.max;
    if (minPrice !== '' && minPrice != null) {
      query = query.gte('price', Number(minPrice));
    }
    if (maxPrice !== '' && maxPrice != null && Number(maxPrice) < 10000) {
      query = query.lte('price', Number(maxPrice));
    }

    // Metraż – obsługuje zarówno płaski format (minArea/maxArea)
    // jak i zagnieżdżony (sizeRange.min/max) z kontekstu
    const minArea = filters.minArea != null ? filters.minArea : filters.sizeRange?.min;
    const maxArea = filters.maxArea != null ? filters.maxArea : filters.sizeRange?.max;
    if (minArea !== '' && minArea != null) {
      query = query.gte('area', Number(minArea));
    }
    if (maxArea !== '' && maxArea != null && Number(maxArea) < 100) {
      query = query.lte('area', Number(maxArea));
    }

    // Liczba pokoi
    if (filters.rooms && filters.rooms.length > 0) {
      const has4Plus = filters.rooms.includes(4);
      const otherRooms = filters.rooms.filter(r => r !== 4);
      if (has4Plus && otherRooms.length > 0) {
        // 4+ plus inne wartości - użyj OR
        query = query.or(`rooms.gte.4,rooms.in.(${otherRooms.join(',')})`);
      } else if (has4Plus) {
        // Tylko 4+
        query = query.gte('rooms', 4);
      } else {
        // Bez 4+ - zwykły in()
        query = query.in('rooms', otherRooms);
      }
    }

    // Kiedy dodane – filtrowanie po created_at
    if (filters.dateAdded) {
      const daysMap = { today: 1, '3days': 3, '7days': 7, '30days': 30 };
      const days = daysMap[filters.dateAdded];
      if (days) {
        const cutoff = new Date();
        cutoff.setDate(cutoff.getDate() - days);
        query = query.gte('created_at', cutoff.toISOString());
      }
    }

    if (filters.limit) {
      query = query.limit(filters.limit);
    }

    // Sortowanie: premium na górze, potem najnowsze
    query = query.order('is_premium', { ascending: false, nullsFirst: false });
    query = query.order('id', { ascending: false });

    const { data, error } = await query;
    if (error) throw error;

    let results = data || [];

    // Udogodnienia – filtrowanie po stronie klienta
    // (kolumna amenities to tablica, Supabase v2 nie obsługuje dobrze contains dla tablic)
    if (filters.amenities && filters.amenities.length > 0) {
      results = results.filter(p => {
        const propAmenities = Array.isArray(p.amenities) ? p.amenities : [];
        return filters.amenities.every(a => propAmenities.includes(a));
      });
    }

    return results;
  },

  // Alias used by useSearch hook
  async search(filters = {}) {
    return this.getFiltered(filters);
  },

  // Add new property
  async add(propertyData) {
    // Check for email duplication ONLY among OTHER users' properties
    const { data: existingProperty, error: checkError } = await supabase
      .from('properties')
      .select('id, email, user_id')
      .eq('email', propertyData.email)
      .neq('user_id', propertyData.user_id) // Sprawdzaj tylko ogłoszenia INNYCH użytkowników
      .single();
    
    if (checkError && checkError.code !== 'PGRST116') { // PGRST116 = no rows found
      throw checkError;
    }
    
    if (existingProperty) {
      throw new Error('Ten adres email jest już używany przez innego użytkownika');
    }

    const { data, error } = await supabase.from('properties').insert([propertyData]).select('id, lat, lng').single();
    if (error) throw error;
    return data;
  },

  // Update property
  async update(id, updates) {
    const { data, error } = await supabase.from('properties').update(updates).eq('id', id);
    if (error) throw error;
    return data;
  },

  // Delete property
  async delete(id) {
    const { data, error } = await supabase.from('properties').delete().eq('id', id);
    if (error) throw error;
    return data;
  }
};

// Tenant profiles service
export const tenantsService = {
  // Get all tenant profiles
  async getAll() {
    const { data, error } = await supabase
      .from('tenant_profiles')
      .select('*')
      .order('created_at', { ascending: false });
    
    
    
    if (error) throw error;
    
    // Debug: check equipment field
    if (data && data.length > 0) {
    }
    
    // Map database fields to component-friendly format
    return (data || []).map(tenant => ({
      id: tenant.id,
      user_id: tenant.user_id || null,
      name: tenant.first_name || '',
      firstName: tenant.first_name,
      avatar: tenant.profile_photo_url || '',
      age: tenant.age,
      profession: tenant.profession || null,
      city: tenant.city,
      preferredDistrict: tenant.preferred_district,
      description: tenant.description,
      roomsWanted: tenant.preferred_rooms,
      priceMin: tenant.price_min,
      priceMax: tenant.price_max,
      priceRange: tenant.price_min && tenant.price_max 
        ? `${tenant.price_min} - ${tenant.price_max} zł` 
        : '',
      areaMin: tenant.area_min,
      areaMax: tenant.area_max,
      peopleCount: tenant.people_count,
      petOwner: tenant.pet_owner,
      smoker: tenant.smoker,
      phone: tenant.phone,
      email: tenant.email,
      moveInDate: tenant.move_in_date,
      equipment: (() => {
        if (Array.isArray(tenant.equipment)) return tenant.equipment;
        if (typeof tenant.equipment === 'string') {
          try {
            const parsed = JSON.parse(tenant.equipment);
            return Array.isArray(parsed) ? parsed : [];
          } catch (e) {
            return [];
          }
        }
        return [];
      })(),
      preferences: [], // Can be populated if needed
      isVerified: tenant.verified || false,
      viewsCount: tenant.views_count || 0,
      references: tenant.references || '',
      createdAt: tenant.created_at
    }));
  },

  // Get filtered tenant profiles
  async getFiltered(filters = {}) {
    let query = supabase.from('tenant_profiles').select('*');
    
    // city is handled client-side for diacritics-insensitive matching
    if (filters.district) {
      query = query.ilike('preferred_district', `%${filters.district}%`);
    }
    query = query.order('created_at', { ascending: false });
    
    const { data, error } = await query;
    if (error) throw error;
    
    let results = data || [];

    // Client-side filters – treat NULL as "not set" (include them)
    if (filters.nonSmoking) {
      results = results.filter(t => t.smoker === false || t.smoker === null || t.smoker === undefined);
    }
    if (filters.noPets) {
      results = results.filter(t => t.pet_owner === false || t.pet_owner === null || t.pet_owner === undefined);
    }

    // Filter by preferred_rooms (rooms the tenant is looking for)
    if (filters.rooms && filters.rooms.length > 0) {
      const has4Plus = filters.rooms.includes(4);
      const otherRooms = filters.rooms.filter(r => r !== 4);
      results = results.filter(t => {
        if (t.preferred_rooms == null) return false;
        const r = Number(t.preferred_rooms);
        if (has4Plus && r >= 4) return true;
        return otherRooms.includes(r);
      });
    }

    // Client-side city filtering – handles Polish diacritics and case (e.g. "wroclaw" matches "Wrocław")
    if (filters.city) {
      const normalizedSearch = normalizeText(filters.city);
      results = results.filter(t => normalizeText(t.city || '').includes(normalizedSearch));
    }
    
    // Map to component format (same fields as getAll)
    return results.map(tenant => ({
      id: tenant.id,
      user_id: tenant.user_id || null,
      name: tenant.first_name || '',
      firstName: tenant.first_name,
      avatar: tenant.profile_photo_url || '',
      age: tenant.age,
      profession: tenant.profession || null,
      city: tenant.city,
      preferredDistrict: tenant.preferred_district,
      description: tenant.description,
      roomsWanted: tenant.preferred_rooms,
      priceMin: tenant.price_min,
      priceMax: tenant.price_max,
      priceRange: tenant.price_min && tenant.price_max 
        ? `${tenant.price_min} - ${tenant.price_max} zł` 
        : '',
      areaMin: tenant.area_min,
      areaMax: tenant.area_max,
      peopleCount: tenant.people_count,
      petOwner: tenant.pet_owner,
      smoker: tenant.smoker,
      phone: tenant.phone,
      email: tenant.email,
      moveInDate: tenant.move_in_date,
      equipment: (() => {
        if (Array.isArray(tenant.equipment)) return tenant.equipment;
        if (typeof tenant.equipment === 'string') {
          try {
            const parsed = JSON.parse(tenant.equipment);
            return Array.isArray(parsed) ? parsed : [];
          } catch (e) {
            return [];
          }
        }
        return [];
      })(),
      preferences: [],
      isVerified: tenant.verified || false,
      viewsCount: tenant.views_count || 0,
      references: tenant.references || '',
      createdAt: tenant.created_at
    }));
  },

  // Add new tenant profile
  async add(tenantData) {
    
    // Check for email duplication
    const { data: existingTenant, error: checkError } = await supabase
      .from('tenant_profiles')
      .select('id, email')
      .eq('email', tenantData.email)
      .single();
    
    
    if (checkError && checkError.code !== 'PGRST116') { // PGRST116 = no rows found
      throw checkError;
    }
    
    if (existingTenant) {
      throw new Error('Ten adres email jest już używany w innym profilu najemcy');
    }

    const { data, error } = await supabase.from('tenant_profiles').insert([tenantData]);
    if (error) {
      throw error;
    }
    return data;
  },

  // Pobierz profile najemcy należące do danego użytkownika
  async getByUserId(userId) {
    if (!userId) return [];
    const { data, error } = await supabase
      .from('tenant_profiles')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });
    if (error) throw error;
    return (data || []).map(tenant => ({
      id: tenant.id,
      user_id: tenant.user_id || null,
      name: tenant.first_name || '',
      firstName: tenant.first_name,
      avatar: tenant.profile_photo_url || '',
      age: tenant.age,
      profession: tenant.profession || null,
      city: tenant.city,
      preferredDistrict: tenant.preferred_district,
      description: tenant.description,
      roomsWanted: tenant.preferred_rooms,
      priceMin: tenant.price_min,
      priceMax: tenant.price_max,
      priceRange: tenant.price_min && tenant.price_max
        ? `${tenant.price_min} - ${tenant.price_max} zł`
        : '',
      areaMin: tenant.area_min,
      areaMax: tenant.area_max,
      peopleCount: tenant.people_count,
      petOwner: tenant.pet_owner,
      smoker: tenant.smoker,
      phone: tenant.phone,
      email: tenant.email,
      moveInDate: tenant.move_in_date,
      equipment: Array.isArray(tenant.equipment) ? tenant.equipment : [],
      isVerified: tenant.verified || false,
      viewsCount: tenant.views_count || 0,
      createdAt: tenant.created_at,
    }));
  },

  // Usuń profil najemcy
  async deleteById(tenantId, userId) {
    const { error } = await supabase
      .from('tenant_profiles')
      .delete()
      .eq('id', tenantId)
      .eq('user_id', userId);
    if (error) throw error;
  },

  // Zaktualizuj profil najemcy
  async update(tenantId, userId, data) {
    const { error } = await supabase
      .from('tenant_profiles')
      .update(data)
      .eq('id', tenantId)
      .eq('user_id', userId);
    if (error) throw error;
  }
};

// Messages service — wysyłanie wiadomości do ogłoszeniodawców
export const messagesService = {
  async send({ propertyId, tenantProfileId, recipientEmail, recipientName, senderUserId, senderName, senderEmail, messageText, listingTitle }) {
    // Zapisz wiadomość w tabeli messages
    // Trigger trg_notify_on_new_message automatycznie doda wpis do notification_queue
    const { data, error } = await supabase
      .from('messages')
      .insert([{
        property_id: propertyId || null,
        tenant_profile_id: tenantProfileId || null,
        recipient_email: recipientEmail,
        recipient_name: recipientName || null,
        sender_user_id: senderUserId,
        sender_name: senderName || null,
        sender_email: senderEmail || null,
        message: messageText,
        listing_title: listingTitle || null
      }]);
    if (error) throw error;
    return data;
  },

  // Pobierz wiadomości odebrane (po email odbiorcy)
  async getReceived(recipientEmail) {
    const { data, error } = await supabase
      .from('messages')
      .select('*')
      .eq('recipient_email', recipientEmail)
      .order('created_at', { ascending: false });
    if (error) throw error;
    return data || [];
  },

  // Oznacz wiadomość jako przeczytaną
  async markAsRead(messageId) {
    const { error } = await supabase
      .from('messages')
      .update({ is_read: true })
      .eq('id', messageId);
    if (error) throw error;
  },

  // Policz nieprzeczytane wiadomości (bez soft-deleted)
  async countUnread(recipientEmail) {
    const { count, error } = await supabase
      .from('messages')
      .select('*', { count: 'exact', head: true })
      .eq('recipient_email', recipientEmail)
      .eq('is_read', false)
      .eq('is_deleted_by_recipient', false);
    if (error) throw error;
    return count || 0;
  }
};
